// Swift `Character` semantics for the text scanners ported from ios/Sources/ViewModels
// (InlineMarkdown, EmojiShortcodes, MentionHighlight, ChannelLinks, BlockMarkdown).
//
// The Swift scanners walk `Array(text)` — extended grapheme clusters — so "the next character"
// is a whole user-perceived character: `*` followed by a combining accent is NOT a `*`, and
// "\r\n" is ONE character. A JS string indexes UTF-16 code units, which would split an emoji
// into two surrogate halves and treat a decorated delimiter as a delimiter. `characterView`
// segments the way Swift does (Intl.Segmenter, grapheme granularity) and remembers where each
// character starts in UTF-16, so every ported scanner can decide on characters exactly as
// Swift does and still REPORT offsets as UTF-16 code units — the coordinate space a JS
// renderer slices with (`text.slice(start, end)`). Swift reported Character counts; for any
// text without surrogate pairs or combining sequences the two are the same number.

export interface CharacterView {
  /** One entry per Swift `Character` (extended grapheme cluster). */
  readonly chars: readonly string[];
  /** `offsets[i]` = UTF-16 offset where `chars[i]` starts; `offsets[chars.length]` = text.length. */
  readonly offsets: readonly number[];
}

let segmenter: Intl.Segmenter | undefined;

/**
 * Below U+0300 nothing extends a grapheme (the first Grapheme_Extend code point is U+0300) and
 * nothing is a surrogate; "\r" is the one pair-former ("\r\n"), so it disables the fast path.
 */
const SINGLE_UNIT_GRAPHEMES = /^[\u0000-\u0009\u000B\u000C\u000E-˿\n]*$/;

/** `text` as Swift `Array(text)` would see it, with each Character's UTF-16 start. */
export function characterView(text: string): CharacterView {
  if (SINGLE_UNIT_GRAPHEMES.test(text)) {
    const chars = text.split('');
    const offsets = new Array<number>(chars.length + 1);
    for (let i = 0; i <= chars.length; i++) offsets[i] = i;
    return { chars, offsets };
  }
  segmenter ??= new Intl.Segmenter(undefined, { granularity: 'grapheme' });
  const chars: string[] = [];
  const offsets: number[] = [];
  for (const s of segmenter.segment(text)) {
    chars.push(s.segment);
    offsets.push(s.index);
  }
  offsets.push(text.length);
  return { chars, offsets };
}

/** `Array(text)` — just the characters. */
export function characters(text: string): string[] {
  return characterView(text).chars as string[];
}

/** Swift `text.count`: the number of Characters. */
export function characterCount(text: string): number {
  return characterView(text).chars.length;
}

function firstScalar(c: string): string {
  const cp = c.codePointAt(0);
  return cp === undefined ? '' : String.fromCodePoint(cp);
}

const ALPHABETIC = /^\p{Alphabetic}$/u;
const NUMERIC = /^\p{N}$/u;
const WHITE_SPACE = /^\p{White_Space}$/u;

/** Swift `Character.isLetter`: the first scalar's `isAlphabetic`. */
export function isLetter(c: string): boolean {
  return ALPHABETIC.test(firstScalar(c));
}

/**
 * Swift `Character.isNumber`: the first scalar has a `numericType`. Approximated with
 * General_Category N (Nd/Nl/No); Swift also counts the handful of Han ideographs that carry a
 * numeric value (一, 二, 万…), which JS regexes cannot name. Only affects word-boundary rules
 * next to those ideographs.
 */
export function isNumber(c: string): boolean {
  return NUMERIC.test(firstScalar(c));
}

/** Swift `Character.isWhitespace`: the first scalar's White_Space property (newlines included). */
export function isWhitespace(c: string): boolean {
  return WHITE_SPACE.test(firstScalar(c));
}

/** Swift `Character.isASCII`: a single ASCII scalar, or "\r\n" (whose asciiValue is "\n"). */
export function isASCII(c: string): boolean {
  if (c === '\r\n') return true;
  return c.length === 1 && c.charCodeAt(0) < 0x80;
}

/** Swift `Character.isLowercase`, ASCII-only — the only way the ported code asks it. */
export function isASCIILowercase(c: string): boolean {
  return c.length === 1 && c >= 'a' && c <= 'z';
}

/**
 * Foundation `trimmingCharacters(in: .whitespaces)`: CharacterSet.whitespaces is General
 * Category Zs plus TAB — NOT newlines.
 */
export function trimWhitespaces(s: string): string {
  return s.replace(/^[\t\p{Zs}]+|[\t\p{Zs}]+$/gu, '');
}
